'use client'

import { useEffect, useRef } from 'react'

interface WaveformVisualizerProps {
    data: number[]
    isRecording: boolean
    isPaused?: boolean
    color?: string
    backgroundColor?: string
    barWidth?: number
    barGap?: number
}

export function WaveformVisualizer({
    data,
    isRecording,
    isPaused = false,
    color = '#3b82f6',
    backgroundColor = 'transparent',
    barWidth = 3,
    barGap = 1,
}: WaveformVisualizerProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const animationRef = useRef<number | null>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const draw = () => {
            const width = canvas.width
            const height = canvas.height

            ctx.fillStyle = backgroundColor
            ctx.fillRect(0, 0, width, height)

            if (data.length === 0) {
                // Draw idle line
                ctx.strokeStyle = color
                ctx.lineWidth = 1
                ctx.beginPath()
                ctx.moveTo(0, height / 2)
                ctx.lineTo(width, height / 2)
                ctx.stroke()
                return
            }

            const totalBarWidth = barWidth + barGap
            const barsCount = Math.floor(width / totalBarWidth)
            const startIndex = Math.max(0, data.length - barsCount)
            const visibleData = data.slice(startIndex)

            ctx.fillStyle = isPaused ? '#9ca3af' : color

            visibleData.forEach((value, index) => {
                const normalizedValue = value / 255
                const barHeight = Math.max(2, normalizedValue * height * 0.8)
                const x = index * totalBarWidth
                const y = (height - barHeight) / 2

                ctx.beginPath()
                ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2)
                ctx.fill()
            })
        }

        draw()

        if (isRecording && !isPaused) {
            const animate = () => {
                draw()
                animationRef.current = requestAnimationFrame(animate)
            }
            animationRef.current = requestAnimationFrame(animate)
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current)
            }
        }
    }, [data, isRecording, isPaused, color, backgroundColor, barWidth, barGap])

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect
                canvas.width = width * window.devicePixelRatio
                canvas.height = height * window.devicePixelRatio
                const ctx = canvas.getContext('2d')
                if (ctx) {
                    ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
                }
            }
        })

        resizeObserver.observe(canvas)

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            className="w-full h-full"
            style={{ width: '100%', height: '100%' }}
        />
    )
}
